import { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { MiniEq } from '@/components/MiniEq';
import { SHOPIFY_PRODUCT_URL } from '@/shopify';
import { goToCheckout } from '@/lib/shopifyClient';
import { HOME } from '@/constants/testIds';
import { scrollToId } from '@/lib/scrollTo';

const HERO_RENDER =
    'https://static.prod-images.emergentagent.com/jobs/026e8d45-2a45-421d-be3c-026a565f857e/images/b65843ec86d18aa359b6aa4e432cc8235f7bde9d7047623d46b7d6cae6f5b2a8.jpeg';

const HeroChip = ({ pos, label, value, eq = false, reduce, delay }) => (
    <motion.div
        className={`absolute hidden lg:block ${pos}`}
        initial={reduce ? false : { opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
        <div className="rounded-xl border border-white/10 bg-black/55 px-4 py-3 backdrop-blur-xl">
            <div className="flex items-center gap-3">
                {eq && <MiniEq />}
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-aqua">{label}</div>
                    <div className="mt-1 text-sm text-zinc-300">{value}</div>
                </div>
            </div>
        </div>
        <div className="ml-6 h-6 w-px bg-gradient-to-b from-aqua/50 to-transparent" aria-hidden="true" />
    </motion.div>
);

export const Hero = () => {
    const reduce = useReducedMotion();
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ['start start', 'end start'],
    });
    const visualY = useTransform(scrollYProgress, [0, 1], [0, 64]);

    const lineReveal = (delay) => ({
        initial: reduce ? false : { y: '110%' },
        animate: { y: 0 },
        transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
    });

    return (
        <section
            id="hero"
            ref={heroRef}
            data-testid={HOME.hero}
            className="relative overflow-hidden pb-24 pt-32 md:pb-32 md:pt-44"
        >
            {/* Restrained aqua glow behind the type */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-0 h-[560px] w-[900px] -translate-x-1/2 rounded-full opacity-20"
                style={{ background: 'radial-gradient(closest-side, rgba(0,229,255,0.35), transparent)' }}
            />

            <div className="relative mx-auto max-w-4xl px-6 text-center">
                <motion.p
                    initial={reduce ? false : { opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.05 }}
                    className="font-mono text-[11px] uppercase tracking-[0.3em] text-aqua"
                >
                    Engine — Studio Copilot for FL Studio
                </motion.p>

                <h1
                    data-testid={HOME.heroHeadline}
                    className="mt-6 text-[2.6rem] font-light leading-[1.06] tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl"
                >
                    <span className="block overflow-hidden pb-1">
                        <motion.span className="block" {...lineReveal(0.15)}>
                            Stay in the session.
                        </motion.span>
                    </span>
                    <span className="block overflow-hidden pb-1">
                        <motion.span className="block" {...lineReveal(0.29)}>
                            Let your <span className="text-aqua">voice</span> take control.
                        </motion.span>
                    </span>
                </h1>

                <motion.p
                    initial={reduce ? false : { opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.55 }}
                    className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-zinc-400 md:text-lg"
                >
                    Meet ENGINE, your voice-controlled Studio Copilot for FL Studio. Keep your focus
                    on the music.
                </motion.p>

                <motion.div
                    initial={reduce ? false : { opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.7 }}
                    className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
                >
                    <a
                        data-testid={HOME.heroCta}
                        href={SHOPIFY_PRODUCT_URL}
                        onClick={goToCheckout}
                        className="group inline-flex h-11 items-center gap-2 rounded-full bg-aqua px-6 text-sm font-medium text-ink transition-all duration-300 hover:shadow-glow hover:brightness-110"
                    >
                        Get ENGINE
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </a>
                    <button
                        data-testid={HOME.heroSecondaryCta}
                        onClick={scrollToId('how-it-works')}
                        className="inline-flex h-11 items-center gap-2 rounded-full border border-white/15 px-6 text-sm text-zinc-300 transition-colors duration-300 hover:border-white/40 hover:text-white"
                    >
                        See how it works
                        <ChevronDown className="h-4 w-4" />
                    </button>
                </motion.div>
            </div>

            {/* Product visual + clearly labelled interface concept */}
            <motion.div
                style={{ y: reduce ? 0 : visualY }}
                className="relative mx-auto mt-16 max-w-5xl px-6 md:mt-24"
            >
                <motion.div
                    initial={reduce ? false : { opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="relative"
                >
                    <div
                        data-testid={HOME.heroVisual}
                        className="relative overflow-hidden rounded-3xl border border-white/10 bg-surface-2"
                    >
                        <img
                            src={HERO_RENDER}
                            alt="ENGINE — studio voice assistant device concept render"
                            className="aspect-[3/2] w-full object-cover opacity-90"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/30" aria-hidden="true" />

                        <div
                            data-testid={HOME.heroStatusPill}
                            className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2.5 rounded-full border border-white/15 bg-black/50 px-4 py-2 backdrop-blur-md"
                        >
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-aqua opacity-60" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-aqua" />
                            </span>
                            <span className="font-mono text-[11px] tracking-[0.25em] text-zinc-200">
                                ENGINE · LISTENING
                            </span>
                        </div>
                    </div>

                    {/* Floating labels — the "clearly labelled interface concept" */}
                    <HeroChip pos="-left-4 top-10 xl:-left-10" label="Wake phrase" value="“Engineer” · “Engine”" reduce={reduce} delay={1.05} />
                    <HeroChip pos="-right-4 top-24 xl:-right-12" label="Optional chime" value="Soft confirmation tone" reduce={reduce} delay={1.2} />
                    <HeroChip pos="-left-4 bottom-24 xl:-left-8" label="Mic level" value="Live input display" eq reduce={reduce} delay={1.35} />
                    <HeroChip pos="-right-4 bottom-10 xl:-right-8" label="Recognition" value="Runs offline" reduce={reduce} delay={1.5} />
                </motion.div>

                {/* Mobile: same labels as a tidy chip row */}
                <div className="mt-6 grid grid-cols-2 gap-3 lg:hidden">
                    {[
                        ['Wake phrase', '“Engineer” · “Engine”'],
                        ['Optional chime', 'Soft confirmation tone'],
                        ['Mic level', 'Live input display'],
                        ['Recognition', 'Runs offline'],
                    ].map(([label, value]) => (
                        <div key={label} className="rounded-xl border border-white/10 bg-surface-2 px-4 py-3">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-aqua">{label}</div>
                            <div className="mt-1 text-sm text-zinc-300">{value}</div>
                        </div>
                    ))}
                </div>
            </motion.div>
        </section>
    );
};

export default Hero;
